import { Inject, Injectable, InjectionToken } from '@angular/core';

import { ChurnZeroConsts } from './churnzero-constants';
import { IChurnZeroService } from './interfaces/churnzero.service.interface';
import { ChurnZeroUser } from './churnzero.module';

export const CHURNZERO_BASE_URL = new InjectionToken<string>(
  'CHURNZERO_BASE_URL'
);

@Injectable({
  providedIn: 'root',
})
export class ChurnZeroService implements IChurnZeroService {
  ChurnZero: any;
  isSync: boolean = false;
  user?: ChurnZeroUser;
  private _churnZeroKey: string = '';
  constructor(
    @Inject(CHURNZERO_BASE_URL) private readonly _churnzero_base_url: string
  ) {
    let cz = document.createElement('script');
    cz.type = 'text/javascript';
    cz.async = true;
    cz.src = this._churnzero_base_url + '/churnzero.js';
    let s: any = document.getElementsByTagName('script')[0];
    if (s?.parentNode) {
      s.parentNode.insertBefore(cz, s);
      cz.onload = () => {
        if (!this.isSync) {
          this._connectWithChurnZero();
        }
        this.isSync = true;
      };
    }
  }
  /**
   * @param {string} accountExternalId same as buyer id.
   * @param {string} contactExternalId same as user id.
   */
  connect = async (user: ChurnZeroUser, churnZeroKey: string) => {
    this.user = user;
    this._churnZeroKey = churnZeroKey;
    if (this.isSync) {
      this._connectWithChurnZero();
    }
    return true;
  };

  private _connectWithChurnZero = async () => {
    await this._isConnected();
    this.ChurnZero.push(['setAppKey', this._churnZeroKey]);
    if (this.user) {
      this.ChurnZero.push(['setContact', this.user.cmp, this.user.id]);
    }
    return true;
  };

  private async _isConnected(retryAttempt: number = 1): Promise<boolean> {
    if (!this.ChurnZero) {
      if (
        !(<any>window).ChurnZero &&
        retryAttempt > ChurnZeroConsts.maxNumberConnAttempts
      ) {
        throw new Error('ChurnZero library not found, check if it was loaded');
      } else if (
        !(<any>window).ChurnZero &&
        retryAttempt <= ChurnZeroConsts.maxNumberConnAttempts
      ) {
        await new Promise((resolve) =>
          setTimeout(resolve, ChurnZeroConsts.connTimeOutMilliseconds)
        );
        const attempt = retryAttempt + 1;
        this._isConnected(attempt);
      } else {
        this.ChurnZero = (<any>window).ChurnZero;
      }
    }
    return true;
  }

  /**
   * @returns {boolean} true
   * @param {string} eventName (Required) This is the unique name of the event (i.e Sent Blog Post). If the Event Name is not found it will be created.
   * @param {string} description (Required) A description of the Event (i.e Blog Title)
   * @param {string} quantity (optional) The number related to this event (Commonly used to track things like email sent, etc)
   * @param {string} customfields (optional) Additional custom fields in a name/value pair. Important Note: At this time, custom fields cannot be created on the fly. Please make sure to request any required custom field(s)
   * @example ChurnZero.push(['trackEvent', 'Email Sent', 'Email Subject', 5, {field1: 'field1Value', field2: 'field2Value'}]);
   */
  sendEvent = async (
    eventName: string,
    description: string,
    customFields?: string,
    quantity?: string
  ) => {
    await this._isConnected();
    this.ChurnZero.push([
      'trackEvent',
      eventName,
      description,
      quantity,
      customFields,
    ]);
    return true;
  };

  /**
   * @returns {boolean} true
   * @param {string} entity (Required)- Can be either 'contact' or 'account'
   * @param {string} name (Required)- Name of the field to be updated
   * @param {string} value (Required)- Value to be used for the update. This value must be type compatible.
   * @example ChurnZero.push(['setAttribute', 'contact', 'Profile Enabled', true]);
   */
  updateAttribute = async (entity: string, name: string, value: string) => {
    this._isConnected();
    this.ChurnZero.push(['setAttribute', entity, name, value]);
    return true;
  };

  /**
   * @summary Disconect with churnzero
   */
  disconnect = () => {
    this._isConnected();
    this.ChurnZero.push([
      'trackEvent',
      ChurnZeroConsts.events.logout,
      ChurnZeroConsts.eventDescriptions.logout,
      1,
      {
        cf_last_login_date: new Date(this.user!.lastLogin).toISOString(),
      },
    ]);
    this.ChurnZero.push(['stop']);
  };
}
